// FILE: src/services/registeredUsersService.js
//
// Wrapper around the admin_registered_users RPC (migrations 049/052/104).
//
// Returns the roster of REAL registered learners — internal/test accounts
// (admins + the telemetry_excluded_user_ids deny-list) are filtered out
// server-side, so the counts here reflect genuine outreach conversion.
//
// Migration 104 added activation-evidence columns (answered_attempts,
// case_answers, answer_event_count, started, activated, guest_answer_evidence).
// Rows from an older RPC are marked `legacy` rather than silently classified
// under the old "any attempt row" definition — see rosterMetrics.js.
//
// Caller authentication happens server-side: the RPC reads the JWT and
// raises unless the caller is in admin_users, so no caller arg is needed.

import { supabase } from './supabaseClient';
import { normalizeRosterRow } from './rosterMetrics';

/**
 * Fetch the registered-users roster, oldest registrations first.
 *
 * @returns {Promise<Array<{
 *   user_id: string, email: string, display_name: string|null,
 *   registered_at: string, last_sign_in_at: string|null,
 *   email_confirmed: boolean, target_exam_id: string|null,
 *   target_exam_date: string|null,    // the date behind "Exam Date Set"
 *   practiced_exam_ids: string|null,  // comma-separated cert ids from attempts
 *   answered_exam_ids: string|null,   // cert ids from the learner's own answer
 *                                     // events — the only exam record that
 *                                     // exists when a session never persisted
 *   attempts: number,                 // persisted question slots (answered or not)
 *   last_attempt_at: string|null,
 *   answered_attempts: number|null,   // answered attempts, repeats included
 *   case_answers: number|null,
 *   answer_event_count: number|null,
 *   started: boolean|null, activated: boolean|null,
 *   guest_answer_evidence: boolean|null,
 *   legacy: boolean,
 * }>>}
 * @throws {Error} on any Supabase failure (including "admin only").
 */
export async function listRegisteredUsers() {
  const { data, error } = await supabase.rpc('admin_registered_users');
  if (error) throw new Error(`List registered users failed: ${error.message}`);
  return (Array.isArray(data) ? data : []).map(normalizeRosterRow);
}
